import { useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IconLoader2 } from '@tabler/icons-react';
import { SearchParam } from '@common/ts';
import { QueryTag } from '@common/ts/enums';
import useTheme from 'chat-ui/src/hooks/useTheme';
import TranscriptsTable from './transcripts_table_data/TranscriptsTable';
import TranslationCoverageBanner from './transcripts_table_data/TranslationCoverageBanner';
import SingleTranscriptData from './SingleTranscriptData';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import { ConversationsTranscriptsProps } from '@/types';
import { useConversationsByStatus, useTranscriptsFilters } from '@/hooks';
import { TRANSCRIPTS_FILTER_PREFIX } from '@/hooks/useTranscriptsFilters';
import useTranslation from '@/hooks/useTranslation';
import { TranscriptsView } from '@/enums';
import { sortingStateToSortConfig } from '@/utils/sortingStateToSortConfig';

function ConversationsTranscripts({
  languages = [],
  showFilters = false,
  onReRun,
  rerunFilterValues = [],
  onFilterDeleteEnableRefetch,
  refetch,
  activeView,
  onViewChange,
  showIncompleteToggle = true,
  showScreenedOutToggle = true,
  projectStatus,
  deletedConversations,
  isCompletedTranscriptsView,
  updateUrlPage,
  tableType,
  isLoading = false,
  ...urlTableProps
}: ConversationsTranscriptsProps) {
  const { project_id: projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const selectedTranscript = !!searchParams.get(SearchParam.SELECTED_TRANSCRIPT);
  const conversationFromQuestionsTab =
    searchParams.get(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB) === 'true';
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const [scrollToConversation, setScrollToConversation] = useState<string | null>(null);

  // Get URL table props
  const { urlCurrentPage, urlSorting } = urlTableProps;

  const sortConfig = sortingStateToSortConfig(urlSorting ?? []);
  const searchFilters = useTranscriptsFilters();

  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const { translatingProjects } = useTranslationsLoadingStore();
  const currentProjectIsTranslating = translatingProjects
    ? translatingProjects?.find((project) => project.projectId === projectId)
    : null;

  const REFETCH_INTERVAL = 10000;

  // Fetch conversations data with search filters
  const {
    completed: completedConversations,
    incomplete: incompleteConversations,
    screenedOut: screenedOutConversations,
    completedCount,
    incompleteCount,
    screenedOutCount,
    totalCount,
    isLoading: isLoadingConversations,
    isError: isErrorConversations,
  } = useConversationsByStatus(
    filterId,
    translationLanguage,
    currentProjectIsTranslating && !selectedTranscript ? REFETCH_INTERVAL : 0,
    !!currentProjectIsTranslating,
    projectId,
    urlCurrentPage,
    activeView,
    selectedTranscript,
    conversationFromQuestionsTab,
    sortConfig,
    searchFilters,
  );

  // Determine which conversations to show based on active view
  const conversationsByView = {
    [TranscriptsView.COMPLETED]: completedConversations,
    [TranscriptsView.INCOMPLETE]: incompleteConversations,
    [TranscriptsView.SCREENED_OUT]: screenedOutConversations,
  };
  // undefined activeView falls back to screened-out, matching the old
  // ternary's else branch exactly.
  const currentConversations = conversationsByView[activeView ?? TranscriptsView.SCREENED_OUT];
  const completedConversationsLength = completedCount;

  // Only show loader on initial load (no data yet)
  // Keep table mounted during filter updates to preserve input focus
  if (isLoadingConversations || isLoading) {
    return (
      <div className="flex items-center mx-auto max-w-[1300px] justify-center w-full h-full p-40">
        <IconLoader2 style={{ color: currentTheme?.primary }} className="animate-spin" size={30} />
      </div>
    );
  }

  // A failed fetch must not masquerade as an empty project — the table's
  // "no results" state is indistinguishable from data loss to the user
  // (TELV1-541). Show an honest error with a retry. Retry also clears the
  // per-column search params: a filter value the backend rejects (e.g.
  // letters in Participant #) would otherwise re-fail every attempt with
  // the pills hidden behind this early return — an unrecoverable page.
  // Never blocks an OPEN transcript: an error on the background list
  // queries must not blank out a conversation the user is reading.
  if (isErrorConversations && !selectedTranscript) {
    return (
      <div className="flex flex-col items-center mx-auto max-w-[1300px] justify-center w-full gap-3 p-40 text-center">
        <p className="text-sm text-gray-600">{lang.get('msg.somethingWentWrong')}</p>
        <button
          type="button"
          className="text-sm font-medium underline"
          style={{ color: currentTheme?.primary }}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            [...next.keys()]
              .filter((key) => key.startsWith(TRANSCRIPTS_FILTER_PREFIX))
              .forEach((key) => next.delete(key));
            setSearchParams(next, { replace: true });
            queryClient.invalidateQueries({ queryKey: [QueryTag.CONVERSATIONS] });
          }}
        >
          {lang.get('msg.retry')}
        </button>
      </div>
    );
  }

  if (!currentConversations) {
    return null;
  }

  const translatedLanguages = ['Original', ...(languages || [])];

  return (
    <div className="w-full mx-auto">
      {/* TELV1-501 — translated views silently omit untranslated
          conversations; the banner surfaces the gap and offers a forced
          retry. Also hosts the once-per-session self-heal re-trigger.
          List view only — a single open transcript is one conversation,
          where a project-wide coverage banner would only confuse. */}
      {!selectedTranscript && (
        <TranslationCoverageBanner
          projectId={projectId}
          translationLanguage={translationLanguage}
          conversationCount={completedConversationsLength}
        />
      )}
      {!selectedTranscript ? (
        <TranscriptsTable
          allConversations={currentConversations}
          projectId={projectId}
          languages={languages}
          completedConversationsLength={completedConversationsLength}
          initialTranslatedLanguages={translatedLanguages}
          showFilters={showFilters}
          onReRun={onReRun}
          rerunFilterValues={rerunFilterValues}
          onFilterDeleteEnableRefetch={onFilterDeleteEnableRefetch}
          refetch={refetch}
          activeView={activeView}
          onViewChange={onViewChange}
          completedCount={completedCount}
          incompleteCount={incompleteCount}
          screenedOutCount={screenedOutCount}
          totalCount={totalCount}
          isLoadingConversations={isLoadingConversations}
          showIncompleteToggle={showIncompleteToggle}
          showScreenedOutToggle={showScreenedOutToggle}
          projectStatus={projectStatus}
          deletedConversations={deletedConversations}
          isCompletedTranscriptsView={isCompletedTranscriptsView}
          scrollToConversation={scrollToConversation}
          onScrollTo={setScrollToConversation}
          updateUrlPage={updateUrlPage}
          {...urlTableProps}
        />
      ) : (
        <SingleTranscriptData
          completedConversationsLength={completedConversationsLength}
          languages={languages}
          projectId={projectId}
          initialTranslatedLanguages={translatedLanguages}
          onScrollTo={setScrollToConversation}
          urlCurrentPage={urlCurrentPage}
          tableType={tableType}
          activeView={activeView}
        />
      )}
    </div>
  );
}

export default ConversationsTranscripts;
